import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { LOCALIDADPROVEEDORES, ILocalidadProveedoresPagingRes, UseMutationsParams, AlertSeverityEnum } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError} from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchLocalidadProveedores = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetLocalidadProveedoresParams) => {
  return useQuery({
    queryKey: ['localidades-proveedores', page, nombre, page_size],
    queryFn: async () => {
      return getLocalidadProveedores({ page, nombre, page_size });
    },
  });
};

export const useGetLocalidadProveedores = (id: string) => {
  return useQuery({
    queryKey: ['localidades-proveedores', id],
    queryFn: async () => {
      return getLocalidadProveedor(id);
    },
  });
};

export const useCreateLocalidadProveedores = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createLocalidadProveedores,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['localidades-proveedores'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'localidad de proveedor creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateLocalidadProveedores = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateLocalidadProveedores,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['localidades-proveedores'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'localidad de proveedores actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetLocalidadProveedoresParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreateLocalidadProveedoresParams = Omit<LOCALIDADPROVEEDORES, 'id_localidad_proveedor'>;
export type UpdateLocalidadProveedoresParams = {
  id: string;
  data: CreateLocalidadProveedoresParams;
};

export const getLocalidadProveedores = async (params?: GetLocalidadProveedoresParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<ILocalidadProveedoresPagingRes>(
    `${urlBackend}api/v1/localidades-proveedores/?${queryParams}`,
    true
  );
};

export const getLocalidadProveedor = async (id:string) => {
  try{
    return await get<LOCALIDADPROVEEDORES>(`${urlBackend}api/v1/localidades-proveedores/${id}/`, true);
  }
  catch(error){
    return handleAxiosError(error);
  }
};

export const createLocalidadProveedores = async (data: CreateLocalidadProveedoresParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<LOCALIDADPROVEEDORES>(`${urlBackend}api/v1/localidades-proveedores/`, data, true);
};

export const updateLocalidadProveedores = async ({ id, data }: UpdateLocalidadProveedoresParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return put<LOCALIDADPROVEEDORES>(`${urlBackend}api/v1/localidades-proveedores/${id}/`, data, true);
}

